/**
 * Error Telemetry - Captures browser errors and sends them to the server
 *
 * This allows Claude to see frontend errors by reading log/frontend_errors.log
 * instead of requiring users to copy/paste console errors.
 *
 * Captures:
 * - console.error() calls
 * - Uncaught exceptions (window.onerror)
 * - Unhandled promise rejections
 * - React ErrorBoundary errors (via reportError())
 */

interface ErrorReport {
  type: 'console_error' | 'uncaught_exception' | 'unhandled_rejection' | 'react_error'
  message: string
  stack?: string
  source?: string
  line?: number
  column?: number
  componentStack?: string
  url: string
  userAgent: string
  timestamp: string
}

// Queue errors during initial load before we can send them
let errorQueue: ErrorReport[] = []
let isInitialized = false

// Debounce sending to avoid flooding the server
let sendTimeout: ReturnType<typeof setTimeout> | null = null
const DEBOUNCE_MS = 1000

/**
 * Send queued errors to the server
 */
async function flushErrors() {
  if (errorQueue.length === 0) return

  const errorsToSend = [...errorQueue]
  errorQueue = []

  try {
    const token = localStorage.getItem('auth_token')
    await fetch('/api/frontend_errors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ errors: errorsToSend }),
    })
  } catch (e) {
    // If sending fails, re-queue the errors (but don't retry infinitely)
    console.warn('[ErrorTelemetry] Failed to send errors to server:', e)
  }
}

/**
 * Queue an error to be sent to the server and optionally show UI
 */
function queueError(error: ErrorReport, showDialog: boolean = false) {
  errorQueue.push(error)

  // Debounce: wait for more errors or send after timeout
  if (sendTimeout) clearTimeout(sendTimeout)
  sendTimeout = setTimeout(flushErrors, DEBOUNCE_MS)

  // Dispatch event for ErrorDialog to show UI
  // Only show dialog for actionable errors (not every console.error from libraries)
  if (showDialog) {
    window.dispatchEvent(new CustomEvent('frontend-error', { detail: error }))
  }
}

/**
 * Format an error into a report
 */
function createErrorReport(
  type: ErrorReport['type'],
  message: string,
  options: Partial<ErrorReport> = {}
): ErrorReport {
  return {
    type,
    message,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    ...options,
  }
}

/**
 * Initialize error telemetry - call this once at app startup
 */
export function initErrorTelemetry() {
  if (isInitialized) return
  isInitialized = true

  // 1. Intercept console.error
  const originalConsoleError = console.error
  console.error = (...args: unknown[]) => {
    // Call original first
    originalConsoleError.apply(console, args)

    // Format the error message
    const message = args
      .map(arg => {
        if (arg instanceof Error) {
          return `${arg.name}: ${arg.message}`
        }
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg)
          } catch {
            return String(arg)
          }
        }
        return String(arg)
      })
      .join(' ')

    // Get stack trace if first arg is an Error
    const stack = args[0] instanceof Error ? args[0].stack : undefined

    queueError(createErrorReport('console_error', message, { stack }))
  }

  // 2. Catch uncaught exceptions (show dialog - these are user-actionable)
  window.addEventListener('error', (event) => {
    // Skip resource loading errors (images, scripts, etc.)
    if (!event.error) return

    const errorReport = createErrorReport('uncaught_exception', event.message, {
      stack: event.error?.stack,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    })
    queueError(errorReport, true) // Show dialog for uncaught exceptions
  })

  // 3. Catch unhandled promise rejections (show dialog - these are user-actionable)
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason
    const message = error instanceof Error
      ? `${error.name}: ${error.message}`
      : String(error)
    const stack = error instanceof Error ? error.stack : undefined

    const errorReport = createErrorReport('unhandled_rejection', message, { stack })
    queueError(errorReport, true) // Show dialog for unhandled rejections
  })

  // Flush any remaining errors before page unload
  window.addEventListener('beforeunload', () => {
    if (errorQueue.length > 0) {
      // Use sendBeacon for reliability during page unload
      // Note: sendBeacon doesn't support custom headers, so auth token isn't sent
      // The endpoint allows anonymous errors anyway
      const blob = new Blob(
        [JSON.stringify({ errors: errorQueue })],
        { type: 'application/json' }
      )
      navigator.sendBeacon('/api/frontend_errors', blob)
    }
  })
}

/**
 * Manually report an error (e.g., from React ErrorBoundary)
 */
export function reportError(
  error: Error,
  componentStack?: string
) {
  queueError(createErrorReport('react_error', `${error.name}: ${error.message}`, {
    stack: error.stack,
    componentStack,
  }))
}

/**
 * Force flush all queued errors immediately
 */
export function flushErrorsNow() {
  if (sendTimeout) clearTimeout(sendTimeout)
  return flushErrors()
}
